import { Gallery, VideoVertical } from 'iconsax-react';
import { type MouseEvent, type PropsWithChildren, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useClickAnyWhere } from 'usehooks-ts';
import useUserPlatform from '@/hooks/use-user-platform';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';

interface Props {
  disabled?: boolean;
  className?: string;
  onPick: (type: FileType, capture?: 'camera' | 'album') => void;
  placement?: 'top' | 'bottom';
  alignment?: 'start' | 'end';
}

export function MediaCaptureMethodPicker({
  className,
  placement = 'top',
  alignment = 'start',
  children,
  disabled,
  onPick,
}: PropsWithChildren<Props>) {
  const dropdownRef = useRef<HTMLDetailsElement>(null);
  const { t } = useTranslation();
  const { isIOS } = useUserPlatform();

  const handlePickMethod = (type: FileType) => {
    dropdownRef.current?.removeAttribute('open');
    onPick(type, 'camera');
  };

  const handleSummonMethod = (e: MouseEvent<HTMLElement>) => {
    if (isIOS) {
      e.preventDefault();
    }
  };

  useClickAnyWhere(() => dropdownRef.current?.removeAttribute('open'));

  return (
    <details
      ref={dropdownRef}
      className={cn('dropdown', {
        'dropdown-top': placement === 'top',
        'dropdown-bottom': placement === 'bottom',
        'dropdown-start': alignment === 'start',
        'dropdown-end': alignment === 'end',
      })}>
      <summary
        role='button'
        className={cn('btn btn-ghost btn-sm', className, { 'btn-disabled': disabled })}
        onClick={handleSummonMethod}>
        {children}
      </summary>

      <ul
        className={cn(
          'menu dropdown-content !fixed !bottom-[5.625rem] z-40 w-48 min-w-max rounded-box bg-base-100 p-2 shadow',
          {
            '-translate-y-2': placement === 'top',
            'translate-y-2': placement === 'bottom',
          },
        )}>
        <li>
          <button
            type='button'
            className='btn btn-ghost justify-between'
            onClick={() => handlePickMethod(FileType.Image)}>
            {t('conversation.attachment-menu.image')}

            <Gallery className='size-6' />
          </button>
        </li>

        <li>
          <button
            type='button'
            className='btn btn-ghost justify-between'
            onClick={() => handlePickMethod(FileType.Video)}>
            {t('conversation.attachment-menu.video')}

            <VideoVertical className='size-6' />
          </button>
        </li>
      </ul>
    </details>
  );
}
